import React, { createContext, useContext, useState, useCallback } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [currentMusic, setCurrentMusic] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const playMusic = useCallback((music) => {
    setCurrentMusic(music);
    setIsPlaying(true);
  }, []);

  const pauseMusic = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const stopMusic = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentMusic(null);
  }, []);

  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleDurationChange = useCallback((dur) => {
    setDuration(dur);
  }, []);

  const handleSeek = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleVolumeChange = useCallback((vol) => {
    setVolume(vol);
    setIsMuted(vol === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      setVolume(1);
      setIsMuted(false);
    } else {
      setVolume(0);
      setIsMuted(true);
    }
  }, [isMuted]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const value = {
    currentMusic,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playMusic,
    pauseMusic,
    stopMusic,
    handleTimeUpdate,
    handleDurationChange,
    handleSeek,
    handleVolumeChange,
    toggleMute,
    handleEnded
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
